import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { RotateCcw } from 'lucide-react';

/**
 * A compact HTML5-canvas signature pad.
 *
 * - Uses pointer events (works with mouse, finger, and Apple Pencil uniformly)
 * - Captures at device pixel ratio to stay crisp on retina displays
 * - Exports as PNG data URL with ~10-20KB typical size
 * - Parent can clear via ref.current.clear() and read via ref.current.toDataURL()
 * - `onChange(hasInk: boolean)` fires when the pad first receives ink (for enabling submit)
 */
const SignatureCanvas = forwardRef(({ height = 160, onChange, disabled = false }, ref) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  // Mirror of `hasInk` for use inside resize closure. We deliberately don't
  // put `hasInk` in resizeCanvas's deps — see comment in resizeCanvas for
  // why (first-stroke wipe bug).
  const hasInkRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Resize the canvas to match its CSS box, multiplied by devicePixelRatio.
  //
  // IMPORTANT: dep array is intentionally empty. If `hasInk` were a dep, the
  // very first pointermove that flips hasInk false->true would trigger a
  // re-render, `resizeCanvas`'s identity would change, the effect below would
  // re-fire mid-stroke, and reassigning canvas.width/height would wipe the
  // 2D context state (path, transform). The in-flight stroke then continues
  // from an undefined subpath, producing the stray diagonal artefact users
  // saw on the first stroke only. The `hasInkRef` lets us still restore
  // ink on window-resize without making the callback reactive.
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Don't resize while the user is actively drawing — a window resize
    // (e.g. mobile keyboard popup) mid-stroke would still nuke the in-progress
    // path even with the dep fix above.
    if (drawingRef.current) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const hadInk = hasInkRef.current;
    const prevData = hadInk ? canvas.toDataURL() : null;
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // 2.8px at CSS size (before DPR scaling). Thicker than the original 2.2
    // for better legibility in the PDF snapshot — older users, shaky hands,
    // and low-DPR phones all benefit. Below ~3.2px we stay visually "pen",
    // above that it starts to look like marker. This is the upper end of pen.
    ctx.lineWidth = 2.8;
    ctx.strokeStyle = '#0f172a';
    if (prevData) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = prevData;
    }
  }, []);

  useEffect(() => {
    resizeCanvas();
    const onResize = () => resizeCanvas();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [resizeCanvas]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e) => {
    if (disabled) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = getPoint(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const pt = getPoint(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPointRef.current = pt;
    if (!hasInkRef.current) {
      // Update ref first so the resize closure sees the right value on the
      // same tick as the state flush. State is for re-rendering UI only.
      hasInkRef.current = true;
      setHasInk(true);
      if (onChange) onChange(true);
    }
  };

  const handlePointerUp = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    try { canvasRef.current.releasePointerCapture?.(e.pointerId); } catch (_) { /* noop */ }
  };

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    hasInkRef.current = false;
    setHasInk(false);
    if (onChange) onChange(false);
  }, [onChange]);

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty: () => !hasInkRef.current,
    toDataURL: () => (hasInkRef.current ? canvasRef.current.toDataURL('image/png') : ''),
  }), [clear]);

  return (
    <div className="w-full">
      <div className="relative rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <canvas
          ref={canvasRef}
          className="block w-full touch-none select-none cursor-crosshair"
          style={{ height: `${height}px` }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
        {!hasInk && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="text-sm text-gray-400 italic">Sign here</span>
          </div>
        )}
        <div className="pointer-events-none absolute bottom-3 left-4 right-4 border-b border-dashed border-gray-300" />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <p className="text-xs text-gray-500">Use your finger, stylus, or mouse to sign.</p>
        <button
          type="button"
          onClick={clear}
          disabled={disabled || !hasInk}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 h-8 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Clear
        </button>
      </div>
    </div>
  );
});

SignatureCanvas.displayName = 'SignatureCanvas';

export default SignatureCanvas;
